import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ActivityCard, BeeTypeCard, CommunityCard, ConsistencyCard, HabitCard, HiveEvolutionCard, IntroCard, MilestonesCard, PercentileCard, SocialCard } from '../../components/recap/RecapCards';
import { RecapShareCard } from '../../components/recap/RecapShareCard';
import { PageHeader } from '../../components/PageHeader';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { Button } from '../../components/ui/Button';
import { recaps } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';
import { useRecapConfig } from '../../hooks/recapConfigStore';
import type { RecapPeriod } from '../../types/engagement';
import { percentileEligible, recapCards, selectBeeArchetype, type RecapCardId } from '../../utils/recap';
import { useScreenInit } from '../../useScreenInit.js';

/**
 * MOB/Stats/Recap — monthly / annual recap as a sequence of story cards ending in a
 * shareable summary. One engine, two periods (Client Review — Revision 03).
 */
export function Recap() {
  const {
    t
  } = useLocale();
  const reduceMotion = useReducedMotion();
  const screenInit = useScreenInit() as {
    recapPeriod?: RecapPeriod;
    recapStep?: RecapCardId;
  };
  const [period, setPeriod] = useState<RecapPeriod>(screenInit.recapPeriod ?? 'month');
  const recap = recaps.find(item => item.period === period) ?? recaps[0];
  // Live shared recap config — Admin edits re-render the recap immediately.
  const config = useRecapConfig();
  const cards = useMemo(() => recapCards(recap), [recap, config]);
  const archetype = useMemo(() => selectBeeArchetype(recap), [recap, config]);
  const initialIndex = Math.max(0, cards.indexOf(screenInit.recapStep ?? 'intro'));
  const [index, setIndex] = useState(initialIndex);
  const defaultCommunity = (r: typeof recap) => r.communityContext.find(item => percentileEligible(item))?.communityId ?? r.communityContext[0]?.communityId ?? '';
  const [communityId, setCommunityId] = useState(defaultCommunity(recap));
  const changePeriod = (next: RecapPeriod) => {
    const nextRecap = recaps.find(item => item.period === next) ?? recaps[0];
    setPeriod(next);
    setIndex(0);
    setCommunityId(defaultCommunity(nextRecap));
  };
  const current = cards[Math.min(index, cards.length - 1)];
  const last = cards.length - 1;
  const renderCard = (id: RecapCardId) => {
    switch (id) {
      case 'intro':
        return <IntroCard recap={recap} data-id="element-3002" data-mp="ww2bgfvbccpl" />;
      case 'beeType':
        return <BeeTypeCard recap={recap} archetype={archetype} data-id="element-3003" data-mp="izytbkxpruth" />;
      case 'hive':
        return <HiveEvolutionCard recap={recap} data-id="element-3004" data-mp="nyrxgmr429gz" />;
      case 'consistency':
        return <ConsistencyCard recap={recap} data-id="element-3005" data-mp="2dc580tptgtqj" />;
      case 'habit':
        return <HabitCard recap={recap} data-id="element-3006" data-mp="2dau48twdflzf" />;
      case 'activity':
        return <ActivityCard recap={recap} data-id="element-3007" data-mp="2iagjt9zo9ebe" />;
      case 'community':
        return <CommunityCard recap={recap} data-id="element-3008" data-mp="2sf6ohnmgmwjb" />;
      case 'social':
        return <SocialCard recap={recap} data-id="element-3009" data-mp="2sdbzny82ik9y" />;
      case 'percentile':
        return <PercentileCard recap={recap} communityId={communityId} onChoose={setCommunityId} data-id="element-3010" data-mp="2ni6gv89yplq3" />;
      case 'milestones':
        return <MilestonesCard recap={recap} data-id="element-3011" data-mp="31a4gmshsa59l" />;
      case 'share':
        return <RecapShareCard recap={recap} archetype={archetype} communityId={communityId} data-id="element-3012" data-mp="2wgi6p8308fhh" />;
    }
  };
  return <div className="px-2 pb-2" data-id="element-3013" data-mp="3b4mo88qbmkyo">
      <PageHeader title={t('eng.recap.title')} subtitle={t('eng.recap.subtitle')} showBack data-id="element-3014" data-mp="3b80u3qy71cav" />

      <SegmentedTabs label={t('eng.recap.title')} value={period} onChange={changePeriod} tabs={[{
      id: 'month',
      label: t('eng.recap.month')
    }, {
      id: 'year',
      label: t('eng.recap.year')
    }]} data-id="element-3015" data-mp="369h4ngke5idr" />

      <ol className="mt-3 flex gap-[3px]" aria-label={t('eng.recap.storyAria', {
      n: index + 1,
      total: cards.length
    })} data-id="element-3016" data-mp="1ptmkyum0g6o0">
        {cards.map((id, i) => <li key={id} className="flex-1" data-id="element-3017" data-mp="1n8f3mbswrhmk" data-mpi={i}>
            <button type="button" onClick={() => setIndex(i)} aria-label={t('eng.recap.storyAria', {
          n: i + 1,
          total: cards.length
        })} aria-current={i === index ? 'step' : undefined} className="flex h-6 w-full items-center" data-id="element-3018" data-mp="1n7n8t374xnxc">
              <span className={`block h-[3px] w-full rounded-pill transition-colors duration-150 ease-brand ${i <= index ? 'bg-gold' : 'bg-sand-line'}`} data-id="element-3019" data-mp="217oaulz4z1ee" />
            </button>
          </li>)}
      </ol>

      <div className="relative mt-1" aria-live="polite" data-id="element-3020" data-mp="1dhkjpe34wu4a">
        <AnimatePresence mode="wait" initial={false} data-id="element-3021" data-mp="1dgspo7p6r4g4">
          <motion.div key={`${period}-${current}`} initial={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          x: -12
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-3022" data-mp="1ia3vq6m6bh6z">
            {renderCard(current)}
          </motion.div>
        </AnimatePresence>
      </div>

      {current !== 'share' && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-3023" data-mp="8at2cc8sjxca">
          <Button variant="outline" disabled={index === 0} onClick={() => setIndex(i => Math.max(0, i - 1))} data-id="element-3024" data-mp="89j3hku46l4l">
            <ChevronLeftIcon size={16} aria-hidden="true" data-id="element-3025" data-mp="4c3fzu5rz5gi" />
            {t('eng.recap.prev')}
          </Button>
          <Button variant="primary" onClick={() => setIndex(i => Math.min(last, i + 1))} data-id="element-3026" data-mp="3qpbsjwkv7myi">
            {t('eng.recap.next')}
            <ChevronRightIcon size={16} aria-hidden="true" data-id="element-3027" data-mp="3lph0vqt9mtce" />
          </Button>
        </div>}
      {current !== 'share' && <Button variant="quiet" fullWidth className="mt-1" onClick={() => setIndex(last)} data-id="element-3028" data-mp="3gsqlz6t763sk">
          {t('eng.recap.toShare')}
        </Button>}
      {current === 'share' && <Button variant="outline" fullWidth className="mt-2" onClick={() => setIndex(i => Math.max(0, i - 1))} data-id="element-3029" data-mp="37rsegejjve58">
          <ChevronLeftIcon size={16} aria-hidden="true" data-id="element-3030" data-mp="2y1aftr2u7m9x" />
          {t('eng.recap.prev')}
        </Button>}
    </div>;
}